// Imports
import React, { useRef, useEffect, useState, useCallback } from "react";
import { useNavigate } from 'react-router-dom';
import { Canvas, useThree, useFrame } from "@react-three/fiber";
import { OrbitControls, Text } from "@react-three/drei";
import * as THREE from "three";
import Select, { components } from "react-select";
import { createPortal } from 'react-dom';

// Constants
const BABY_BLUE = "#89CFF0";
const REG_BLUE = "#348FD9";
const DEEP_BLUE = "#222b36";
const QUIT_RED = "#E3242B";
const DEFAULT_CAMERA_POS = [0, 0, 40];

const DEFAULT_SECTION_COLORS = [
  "#4dd7ee", // neon adolescent blue
  "#ffe700", // neon yellow
  "#f000ff", // neon pink
  "#c493ff", // light purple
  "#74ee15", // neon green
  "#ffaa00", // neon orange
  "#fe0000", // neon red
  "#00ffbc", // fluorescent green
  "#001eff", // neon indigo
  "#aaff00", // neon lime
  // ...add more if you expect lots of sections
];

// Helper function to get wiki frequency label
const getWikiFrequencyLabel = (freq) => {
  if (freq > 1_000_000) return "common";
  if (freq > 100_000) return "uncommon";
  if (freq > 10_000) return "rare";
  if (freq > 1_000) return "very rare";
  if (freq > 100) return "epic";
  return "legendary";
};

// Adding a browser size listener for dynamic browser resizing
function useWindowSize() {
  
  const getSize = () => {
    const styles = getComputedStyle(document.documentElement);
    let availableWidth = parseInt(styles.getPropertyValue('--available-width'));
    let availableHeight = parseInt(styles.getPropertyValue('--available-height'));
    
    // More robust fallback - check for NaN specifically
    if (isNaN(availableWidth) || availableWidth === 0) {
      availableWidth = window.innerWidth;
    }
    if (isNaN(availableHeight) || availableHeight === 0) {
      availableHeight = window.innerHeight;
    }

    return {
      width: window.innerWidth,     // Original full viewport width
      height: window.innerHeight,   // Original full viewport height
      dwidth: availableWidth,       // Available width (safe-area aware)
      dheight: availableHeight      // Available height (safe-area aware)
    };
  };

  const [windowSize, setWindowSize] = useState(getSize);

  useEffect(() => {
    // Initial setup with delay to ensure CSS variables are ready
    const initialTimeout = setTimeout(() => {
      setWindowSize(getSize());
    }, 50); // Small delay for CSS variables to be set

    const handleResize = () => {
      // Add a small delay to ensure CSS variables are updated
      setTimeout(() => {
        setWindowSize(getSize());
      }, 10);
    };
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    
    return () => {
      clearTimeout(initialTimeout);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  return windowSize;
}

// Helper: colored circle for dropdown options
function ColorCircle({ color }) {
  return (
    <span
      style={{
        display: "inline-block",
        width: 12,
        height: 12,
        borderRadius: 12,
        backgroundColor: color,
        marginRight: 0,
        verticalAlign: "middle",
        border: "1px solid #00000033"
      }}
    />
  );
}

// Dropdown for choosing section color
const colorOptions = [
  { value: "", label: <span className="transparent-label" style={{ fontSize: 19.6 }}>∅</span> }, // Transparent
  ...DEFAULT_SECTION_COLORS.map(c => ({ value: c, label: <ColorCircle color={c} /> }))
];

// Dropdown for song sections
function SectionColorDropdown({ section, color, onChange }) {
  const selectedOption = colorOptions.find(o => o.value === color);

  const customStyles = {
    control: (provided) => ({
      ...provided,
      width: '75px',
    }),
    menu: (provided) => ({
      ...provided,
      maxHeight: '80px',
      overflowY: 'auto !important',
      touchAction: 'pan-y !important', // Allow vertical touch scrolling
      WebkitOverflowScrolling: 'touch',
      position: 'absolute',
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: '80px',
      overflowY: 'auto',
    }),
  };

  return (
    <Select
      key={color || ''}  // Force re-render on color change or transparent ('')
      value={selectedOption}
      onChange={selected => onChange(section, selected.value)}
      options={colorOptions}
      isSearchable={false}
      classNames={{
        control: () => "dropdown-control",
        valueContainer: () => "dropdown-value-container",
        indicatorsContainer: () => "dropdown-indicators-container",
        dropdownIndicator: () => "dropdown-dropdown-indicator",
        menu: () => "dropdown-menu",
        option: () => "dropdown-option",
        singleValue: () => "dropdown-single-value"
      }}
      components={{
        IndicatorSeparator: () => null,
        Menu: (props) => <components.Menu {...props} id="color-dropdown-menu" /> // Menu ID
      }}
      menuShouldBlockScroll={false}  // Don't block page scroll
      captureMenuScroll={false}      // Allow menu scroll events to bubble
      menuPosition="absolute"        // Better positioning
      menuPlacement="auto"           // Auto-detect best placement
      menuShouldScrollIntoView={false} // Prevent viewport jumping
      styles={{
        ...customStyles,
        menuList: (provided) => ({
          ...provided,
          maxHeight: '80px',
          overflowY: 'auto',   
        }),
      }}
    />
  );
}

function MasterColorDropdown({ expandedSectionsColors = [], color, onChange }) {
  // Compute if all sections have the same color
  const uniqueColors = new Set(expandedSectionsColors.filter(c => c)); // Ignore empty
  const isUniform = uniqueColors.size === 1;
  const uniformColor = isUniform ? [...uniqueColors][0] : null;
  const isAllTransparent = expandedSectionsColors.every(c => !c); // All empty/transparent
  const showRainbow = !isUniform && !isAllTransparent;

  // Custom rainbow option (not added to colorOptions so it's not selectable)
  const rainbowOption = { value: 'mixed', label: <span style={{ fontSize: 16 }}>🍭</span> };

  // Selected option: only if uniform and matches color (or all transparent)
  const selectedOption = showRainbow
    ? rainbowOption  // Use custom rainbow when mixed
    : colorOptions.find(o => o.value === (isAllTransparent ? "" : uniformColor));

  // Indicator style (applied via class)
  const indicatorClass = showRainbow ? "rainbow" : (isAllTransparent ? "transparent" : "");

  const customStyles = {
    control: (provided) => ({
      ...provided,
      width: '75px',
    }),
    menu: (provided) => ({
      ...provided,
      maxHeight: '80px',
      overflowY: 'auto !important',
      touchAction: 'pan-y !important', // Allow vertical touch scrolling
      WebkitOverflowScrolling: 'touch',
      position: 'absolute',
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: '80px',
      overflowY: 'auto',
    }),
  };

  return (
    <Select
      key={color || ''}  // Force re-render on color change or transparent ('')
      value={selectedOption} 
      onChange={selected => onChange(selected.value)} // Parent handles updating all sections
      options={colorOptions}
      isSearchable={false}
      classNames={{
        control: () => "dropdown-control",
        valueContainer: () => "dropdown-value-container",
        indicatorsContainer: () => "dropdown-indicators-container",
        dropdownIndicator: () => `dropdown-dropdown-indicator ${indicatorClass}`, // Apply class for indicator
        menu: () => "dropdown-menu",
        option: () => "dropdown-option",
        singleValue: () => "dropdown-single-value"
      }}
      components={{
        IndicatorSeparator: () => null,
        Menu: (props) => <components.Menu {...props} id="color-dropdown-menu" /> // Menu ID
      }}
      menuShouldBlockScroll={false}  // Don't block page scroll
      captureMenuScroll={false}      // Allow menu scroll events to bubble
      menuPosition="absolute"        // Better positioning
      menuPlacement="auto"           // Auto-detect best placement
      menuShouldScrollIntoView={false} // Prevent viewport jumping
      styles={{
        ...customStyles,
        menuList: (provided) => ({
          ...provided,
          maxHeight: '80px',
          overflowY: 'auto',   
        }),
      }}
    />
  );
}

// Single song entry in the dashboard
function SongEntry({
  song,
  isExpanded,
  toggleExpanded,
  onSectionColorChange,
  onMasterColorChange,
  onDelete,
  expandedSectionsColors,
}) {
  const sections = Object.keys(song.sections || {});

  // Helper function to get year from release date
  const getYearFromDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      return date.getFullYear();
    } catch (error) {
      return null;
    }
  };  

  return (
    <div style={{ marginBottom: 20, borderBottom: "1px solid #555", paddingBottom: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div 
          style={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            WebkitTapHighlightColor: 'transparent'
          }} 
          onClick={toggleExpanded}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggleExpanded();
            }
          }}
          tabIndex={0}
          role="button"
          aria-expanded={isExpanded}
          aria-label={isExpanded ? "Collapse song details" : "Expand song details"}
        >
          <span style={{ marginLeft: 10, marginRight: 16, fontSize: 20, color: "#ccc" }}>
            {isExpanded ? "📂" : "📁"}
          </span>
          <div style={{ fontSize: 20, color: "#fff", marginRight: 16 }}>
            {/* Song name */}
            <strong>{song.name}</strong>
            {/* Artist name */}
            <div style={{ fontSize: 16, color: "#ccc" }}>
              {song.artist} {/* | {song.language} */}
            </div>
            {/* Additional metadata */}
            <div style={{ fontSize: 12, color: "#aaa", marginTop: 2, marginLeft: 4 }}>
              📈 {(() => {
                const parts = [];
                parts.push(
                  <React.Fragment key="word_count">
                    <strong>Word Count:</strong> {song.word_count || 'n/a'}
                  </React.Fragment>
                );
                if (song.genre && song.genre.length > 0) {
                  parts.push(
                    <React.Fragment key="genre">
                      <strong>Genre:</strong> {song.genre[0]}
                    </React.Fragment>
                  );
                } else {
                  parts.push(
                    <React.Fragment key="genre">
                      <strong>Genre:</strong> {'n/a'}
                    </React.Fragment>
                  );
                }
                if (song.release_date) {
                  parts.push(
                    <React.Fragment key="year">
                      <strong>Year Released:</strong> {getYearFromDate(song.release_date) || 'n/a'}
                    </React.Fragment>
                  );
                }
                if (song.label) {
                  parts.push(
                    <React.Fragment key="label">
                      <strong>Record Label:</strong> {song.label || 'n/a'}
                    </React.Fragment>
                  );
                }
                
                // Join with " | " separator
                return parts.reduce((prev, curr, index) => 
                  index === 0 ? [curr] : [...prev, ' | ', curr], []
                );
              })()}
            </div>
          </div>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation(); // Prevent triggering header toggle
            onDelete(song.id);
          }}
          title="Delete Song"
          style={{
            backgroundColor: QUIT_RED,
            border: "none",
            color: "#fff",
            cursor: "pointer",
            marginRight: 10,
            fontFamily: "'Nunito', sans-serif",
            fontSize: 22,
            fontWeight: "bold",
            borderRadius: 4,
            width: 26,
            height: 26,
            lineHeight: "20px",
            textAlign: "center",
            userSelect: "none",
          }}
        >
          ×
        </button>
      </div>

      {isExpanded && (
        <div style={{ margin: "8px 8px 0px 18px", paddingLeft: 35, borderLeft: "4px dotted #555" }}> {/* Moved right + vertical line */}
          {/* Table-like structure for sections */}
          <div style={{
            display: "flex",
            fontWeight: "bold",
            fontSize: 18,
            borderBottom: "1px solid #777",
            paddingBottom: 4,
            marginBottom: 4,
            alignItems: "center"
          }}>
            <div style={{ flex: 1, color: "#fff" }}>Section</div> {/* Takes rest of space */}
            <div style={{ flex: "0 0 auto", display: "flex", justifyContent: "flex-end", color: "#fff", alignItems: "center" }}> {/* Fit content + right align */}
              <span>Color&nbsp;&nbsp;</span>
              <MasterColorDropdown
                expandedSectionsColors={expandedSectionsColors}
                color={song.masterColor || ""}
                onChange={(value) => onMasterColorChange(song.id, value)}
              />
            </div>
          </div>
          {sections.map((sectionName, index) => (
            <div
              key={`${sectionName}-${song.sectionColors[sectionName] || ''}`}  // Force re-render on color change
              style={{ display: "flex", alignItems: "center", marginBottom: 6 }}
            >
              <span style={{ flex: 1, color: "#ddd", fontSize: 16 }}>{sectionName}</span>
              <div style={{ flex: "0 0 auto", display: "flex", justifyContent: "flex-end" }}>
                <SectionColorDropdown
                  section={sectionName}
                  color={song.sectionColors[sectionName] ?? DEFAULT_SECTION_COLORS[0]}  // Preserve "" with ??
                  onChange={(section, color) => onSectionColorChange(song.id, section, color)}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Dashboard component
function Dashboard({
  songs,
  setSongs,
  onSectionColorChange,
  onMasterColorChange,
  onDeleteSong,
  collapsed,
  setCollapsed,
  onClear,
  newlyAddedSongId,  // Receive state
  setNewlyAddedSongId,  // Receive setter
}) {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [expandedSongs, setExpandedSongs] = useState({}); // { songId: true/false }

  // Auto-expand newly added song
  useEffect(() => {
    if (newlyAddedSongId) {
      setExpandedSongs(prev => ({ ...prev, [newlyAddedSongId]: true }));
      setNewlyAddedSongId(null);  // Reset to avoid re-triggering
    }
  }, [newlyAddedSongId, setNewlyAddedSongId]);

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const wideScreen = windowWidth >= 900;

  // Toggle expansion for a song
  const toggleSongExpansion = (songId) => {
    setExpandedSongs(prev => ({ ...prev, [songId]: !prev[songId] }));
  };

  const dashboardStyle = {
    position: "fixed",
    backgroundColor: "rgba(40, 40, 40, 0.9)",
    fontFamily: "'Nunito', sans-serif",
    fontWeight: 400,
    color: "#ddd",
    boxShadow: "0 0 15px #000a",
    transition: 'all 0.3s cubic-bezier(.4,2,.3,1)',
    overflowY: "auto",
    zIndex: 9999,
    userSelect: "none",
    WebkitUserSelect: "none",
    MozUserSelect: "none",
    msUserSelect: "none",
    overflow: "visible",  // Explicitly allow overflow for children if needed
    ...(wideScreen
      ? {
          top: 0,
          right: 0,
          height: "100vh",
          width: collapsed ? 0 : 400,  // Collapse to 0
          borderTopLeftRadius: 8,
          borderBottomLeftRadius: 8,
          display: "flex",
          flexDirection: "column",
        }
      : {
          left: 0,
          bottom: 0,
          width: "100vw",
          height: collapsed ? 0 : "40vh",
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          display: "flex",
          flexDirection: "column",
        }),
  };
  
  const tabStyleBase = {
    position: "fixed",  // Position relative to viewport, not parent
    cursor: "pointer",
    backgroundColor: "#333",
    color: "#eee",
    fontWeight: "bold",
    userSelect: "none",
    WebkitUserSelect: "none",
    MozUserSelect: "none",
    msUserSelect: "none",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    transition: 'all 0.3s cubic-bezier(.4,2,.3,1)',
    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",  // Detached shadow
    zIndex: 10000,  // Above dashboard
  };
  
  const tabStyle = wideScreen
    ? {
        ...tabStyleBase,
        top: `calc(20px + env(safe-area-inset-top, 0px))`,
        right: collapsed ? 0 : 400,  // At screen right when collapsed, at dashboard's left edge when expanded
        borderTopLeftRadius: 8,  // Rounded on sticking side
        borderBottomLeftRadius: 8,
        width: 40,
        height: 60,
      }
    : {
        ...tabStyleBase,
        right: 20,
        bottom: collapsed ? 0 : "40vh", // Match your dashboard's height
        borderTopLeftRadius: 8,  // Rounded on sticking side
        borderTopRightRadius: 8,
        width: 60,
        height: 40,
      };
  
  const tabSymbol = wideScreen ? (collapsed ? "◀" : "▶") : (collapsed ? "▲" : "▼");

  const headerStyle = {
    flexShrink: 0,
    padding: "10px 16px",
    fontWeight: "bold",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: "1px solid #555",
  };

  const songsContainerStyle = {
    flexGrow: 1,
    overflowY: "auto",
    padding: 16,
  };

  return (
    <>
      <div style={tabStyle} onClick={() => setCollapsed(c => !c)} title="Toggle Dashboard">
        {tabSymbol}
      </div>

      <div style={dashboardStyle}>
        {!collapsed && (
          <>
            <div style={headerStyle}>
              <div style={{
                fontSize: 24,
                color: "#fff",
                fontFamily: "'Nunito', sans-serif",
                fontWeight: 'bold',
              }}>
                📟&nbsp;&nbsp;Dashboard
              </div>
              <button
                onClick={onClear}  // Call full reset
                title="Clear Canvas"
                style={{
                  backgroundColor: QUIT_RED,
                  color: "#fff",
                  fontFamily: "'Nunito', sans-serif",
                  fontWeight: "bold",
                  fontSize: 14,
                  border: "none",
                  borderRadius: 4,
                  padding: "4px 8px",
                  cursor: "pointer",
                }}
              >
                Clear All
              </button>
            </div>

            <div style={songsContainerStyle}>
              {songs.length === 0 && (
                <div style={{ color: "#999", fontStyle: "italic" }}>
                  No songs currently visualized.
                </div>
              )}

              {songs.map(song => {
                // Compute expanded sections' colors (assume all sections are "expanded" for now)
                const expandedSectionsColors = Object.keys(song.sections || {})
                  .filter(section => true) // Replace with actual expansion check if available, e.g., expandedSections.includes(section)
                  .map(section => song.sectionColors[section])
                  .filter(Boolean); // Exclude empty/null

                return (
                  <SongEntry
                    key={song.id}
                    song={song}
                    isExpanded={!!expandedSongs[song.id]} // Pass current expanded state
                    toggleExpanded={() => toggleSongExpansion(song.id)} // Pass toggle function
                    onSectionColorChange={onSectionColorChange}
                    onMasterColorChange={onMasterColorChange}
                    onDelete={onDeleteSong}
                    expandedSectionsColors={expandedSectionsColors} // New prop
                  />
                );
              })}
            </div>
          </>
        )}
      </div>
    </>
  )
}

/* // Block all scroll events not targeting the canvas, controls, or about section
function useBlockPageScroll(canvasId = 'orbit-canvas') {
  useEffect(() => {
  const blockEvent = e => {
      // Allow if target is within the canvas OR about section
      if (
      e.target.closest(`#${canvasId}`) ||
      e.target.closest(`#about-section`) ||
      e.target.closest(`#controls-section`) ||
      e.target.closest(`#song-search-input`)
      ) {
      return;
      }
      e.preventDefault();
      e.stopPropagation();
  };

  window.addEventListener("wheel", blockEvent, { passive: false });
  window.addEventListener("touchmove", blockEvent, { passive: false });
  window.addEventListener("keydown", blockEvent);

  document.body.style.overflow = 'hidden';
  document.body.style.touchAction = 'none';

  return () => {
      window.removeEventListener("wheel", blockEvent);
      window.removeEventListener("touchmove", blockEvent);
      window.removeEventListener("keydown", blockEvent);
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
  };
  }, [canvasId]);
} */

// Double-tap on mobile
function useDoubleTap(callback, ms = 350, canvasId = 'orbit-canvas') {
  useEffect(() => {
  let lastTap = 0;
  let lastTouchEndTime = 0;
  let touchMoved = false;

  const handleTouchStart = (e) => {
      touchMoved = false;
      // Only care about single-finger taps
      if (e.touches.length > 1) touchMoved = true;
      // Save start position to check for movement
      e.target.dataset.startX = e.touches[0].clientX;
      e.target.dataset.startY = e.touches.clientY;
  };

  const handleTouchMove = (e) => {
      // If movement is more than a few pixels, it's not a tap
      const sx = parseInt(e.target.dataset.startX, 10);
      const sy = parseInt(e.target.dataset.startY, 10);
      const dx = Math.abs(e.touches.clientX - sx);
      const dy = Math.abs(e.touches.clientY - sy);
      if (dx > 10 || dy > 10) touchMoved = true;
  };

  const handleTouchEnd = (e) => {
      // If not one finger, or moved, do nothing
      if (touchMoved || e.changedTouches.length !== 1) return;

      const now = Date.now();
      if (now - lastTap < ms && now - lastTouchEndTime > 40) {
      callback(e);
      lastTap = 0;
      } else {
      lastTap = now;
      }
      lastTouchEndTime = now;
  };

  const el = document.getElementById(canvasId);
  if (!el) return;

  el.addEventListener('touchstart', handleTouchStart);
  el.addEventListener('touchmove', handleTouchMove);
  el.addEventListener('touchend', handleTouchEnd);

  return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
  };
  }, [callback, ms, canvasId]);
}  

// Main song search
function SongSearch({
  selectedSongId,
  setSelectedSongId,
  onSelect,
  query,  // Lifted props
  setQuery,
  selectedSongText,
  setSelectedSongText
}) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const { width, height, dwidth } = useWindowSize();

  const debounceTimerRef = useRef(null);
  async function performSearch(searchQuery) {
  // Search function
    if (!searchQuery.trim()) {
      setResults([]);
      return;
    }
    
    setLoading(true);
    try {
      const resp = await fetch(`/.netlify/functions/songSearch?q=${encodeURIComponent(searchQuery)}`);
      const data = await resp.json();
      
      const hits = data.response?.hits ?? [];
      
      const filteredHits = hits.filter(({ result }) => {
        const artistName = result.primary_artist?.name || "";
        return !(
          artistName.startsWith("Genius ") ||
          artistName.endsWith(" Genius") ||
          artistName.includes(" Genius ")
        );
      });
      
      setResults(filteredHits);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }

  // Search function
  async function handleSearch(e) {
    e.preventDefault();
    if (!query) return;
    
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);

    }
    
    await performSearch(query);
    setShowDropdown(true);
  }

  const handleInputChange = (e) => {
    const newQuery = e.target.value;
    setQuery(newQuery);
    setSelectedSongText("");
    
    if (newQuery.trim()) {
      setResults([]);
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
      setResults([]);
    }
    
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    if (newQuery.trim()) {
      debounceTimerRef.current = setTimeout(() => {
        performSearch(newQuery);
      }, 500);
    }
  };
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Handle click outside dropdown
  React.useEffect(() => {
    function handleClick(e) {
      if (!e.target.closest("#song-search-inline-dropdown")) setShowDropdown(false);
    }
    if (showDropdown) {
      window.addEventListener("mousedown", handleClick);
      return () => window.removeEventListener("mousedown", handleClick);
    }
  }, [showDropdown]);

  return (
    <div
      id="song-search-inline"
      style={{
        position: "relative",
        width: dwidth < 450 
          ? Math.max(dwidth - 20 - 2 - 12 - 3 - 1 - 1 - 10 - 8 - 1 - 60 - 1 - 12 - 2 - 20, 150)  // Available space minus margins, border weights, paddings, gaps, and Add button width
          : (width < 900 ? 210 : 250),
        minHeight: 30,
        fontFamily: "'Sniglet', sans-serif",
        fontWeight: 400,
        borderRadius: 12,
        background: "black",
        boxShadow: "0 2px 6px rgba(0,0,0,0.18)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <form
        onSubmit={handleSearch}
        style={{
          display: "flex",
          width: "100%",
          height: "100%",
          border: "1px solid #348FD9",
          borderRadius: 12,
          alignItems: "center",
        }}
      >
        <input
          id="song-search-input"
          type="text"
          placeholder="Search a song"
          value={selectedSongText || query}
          spellCheck={false}
          onChange={handleInputChange}
          onClick={() => {
            if (selectedSongText) {
              setSelectedSongText("");
              setSelectedSongId(null);
              setQuery("");
            }
          }}
          style={{
            flex: 1,
            padding: "0 2px 0 12px", // input gets the side padding
            border: "none",
            background: "transparent",
            color: "white",
            fontSize: 18,
            height: 30,
            outline: "none",
            minWidth: 75,
          }}
        />
        <button
          type="submit"
          title="Search Song"
          style={{
            border: "none",
            background: REG_BLUE,
            color: "#fff",
            fontWeight: 700,
            fontSize: 22,
            height: 30,
            width: 40,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "0 10px 10px 0", // rounded right corners only
            transition: "background 0.15s",
            userSelect: "none",
            WebkitUserSelect: "none",
            MozUserSelect: "none",
            msUserSelect: "none",
          }}
          // For mouse (desktop)
          onMouseEnter={e => e.currentTarget.style.background = "#2071B2"}
          onMouseLeave={e => e.currentTarget.style.background = REG_BLUE}
          // For touch (mobile)
          onTouchStart={e => {
            e.currentTarget.style.background = "#2071B2";
            e.currentTarget.classList.add('touch-active');
          }}
          onTouchEnd={e => {
            e.currentTarget.style.background = REG_BLUE;
            e.currentTarget.classList.remove('touch-active');
          }}
          onTouchCancel={e => {
            e.currentTarget.style.background = REG_BLUE;
            e.currentTarget.classList.remove('touch-active');
          }}
        >
          🔍
        </button>
      </form>
      {loading && (
        <div
          style={{
            padding: "8px 0 2px 10px",
            color: REG_BLUE,
            fontSize: 14,
            background: "transparent", // transparent background
            textAlign: "left",         // align text left
            userSelect: "none",
            WebkitUserSelect: "none",
            MozUserSelect: "none",
            msUserSelect: "none",
          }}
        >
          Loading…
        </div>
      )}
      {showDropdown && results.length > 0 && (
        <div
          id="song-search-inline-dropdown"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            width: "100%",
            background: DEEP_BLUE,
            opacity: 0.99,
            borderRadius: 10,
            boxShadow: "0 4px 24px rgba(0,0,0,0.22)",
            zIndex: 20,
            marginTop: 4,
            maxHeight: height * 0.35,
            overflowY: "auto",
          }}
        >
          {results.map(({ result }) => (
            <div
              key={result.id}
              onClick={() => {
                const text = `${result.title} - ${result.primary_artist?.name || ""}`;
                setSelectedSongText(text);
                setSelectedSongId(result.id);
                setShowDropdown(false);
                if (onSelect) onSelect(result);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                padding: "12px 16px",
                cursor: "pointer",
                color: "white",
                borderBottom: "1.5px solid #313c4a",
                transition: "background 0.13s",
                userSelect: "none",
                WebkitUserSelect: "none",
                MozUserSelect: "none",
                msUserSelect: "none",
              }}
              // For mouse (desktop)
              onMouseEnter={e => e.currentTarget.style.background = "#313c4a"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              // For touch (mobile)
              onTouchStart={e => {
                e.currentTarget.style.background = "#313c4a";
                e.currentTarget.classList.add('touch-active');
              }}
              onTouchEnd={e => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.classList.remove('touch-active');
              }}
              onTouchCancel={e => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.classList.remove('touch-active');
              }}
            >
              <img
                src={result.song_art_image_thumbnail_url}
                alt="art"
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 6,
                  objectFit: "cover",
                  marginRight: 14,
                }}
              />
              <div>
                <div style={{ fontSize: 18, fontWeight: 400 }}>
                  {result.title}
                </div>
                <div style={{ fontSize: 14, color: BABY_BLUE, fontWeight: 400 }}>
                  {result.primary_artist?.name || ""}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {showDropdown && results.length === 0 && !loading && (
        <div
          style={{
            padding: "8px 0 2px 10px",
            color: REG_BLUE,
            fontSize: 14,
            background: "transparent", // transparent background
            textAlign: "left",         // align text left
            userSelect: "none",
            WebkitUserSelect: "none",
            MozUserSelect: "none",
            msUserSelect: "none",
          }}
        >
          No results found!
        </div>
      )}
    </div>
  );
}

// PulseText for loading
function PulseText({ children, speed = 1 }) {
    const [time, setTime] = useState(0);

    useEffect(() => {
    let frameId;
    const animate = () => {
        setTime(prev => prev + 0.02 * speed);
        frameId = requestAnimationFrame(animate);
    };
    animate();
    return () => cancelAnimationFrame(frameId);
    }, [speed]);

    // Opacity oscillates between 0.25 and 1
    const opacity = 0.625 + 0.375 * Math.sin(time * Math.PI);
    // Scale oscillates between 1 and 1.05 (or adjust as desired)
    //const scale = 1 + 0.05 * Math.sin(time * Math.PI);

    return (
    <div style={{
        opacity,
    }}>
        {children}
    </div>
    );
}

// Helper function: getting text color info for gradient purposes
const getTextColorInfo = (colors) => {
  if (!colors || colors.length === 0) return { color: "#fff", isMultiColor: false };
  
  const validColors = colors.filter(c => c && c !== "" && c !== "transparent");
  
  if (validColors.length === 0) return { color: "#fff", isMultiColor: false }; // All transparent
  if (validColors.length === 1) return { color: validColors[0], isMultiColor: false }; // Single color
  
  // Multiple colors
  return {
    color: validColors[0], // Primary color for 3D text fallback
    isMultiColor: true,
    validColors: validColors,
    gradientStyle: {
      background: `linear-gradient(90deg, ${validColors.join(", ")})`,
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundClip: "text",
      display: "inline-block",
      color: validColors[0] // Fallback
    }
  };
};

// Main visualization component
function MusicViz({ 
  selectedSongId,
  setSelectedSongId,
  songs,
  setSongs,
  isDashboardCollapsed,
  setIsDashboardCollapsed,
  firstTime,
  setFirstTime,
  searchQuery,  // Receive lifted states
  setSearchQuery,
  selectedSongText,
  setSelectedSongText,
  setNewlyAddedSongId,  // Receive setter
}) {
  const navigate = useNavigate();
  const [aggregatedWords, setAggregatedWords] = useState([]);
  const [aggregatedPositions, setAggregatedPositions] = useState([]);
  const [aggregatedColors, setAggregatedColors] = useState([]);
  const [aggregatedSections, setAggregatedSections] = useState([]);
  const [aggregatedRadii, setAggregatedRadii] = useState([]);

  const [selectedWordInfo, setSelectedWordInfo] = useState(null);
  const [infoBoxPosition, setInfoBoxPosition] = useState({ x: 0, y: 0 });
  const [aggregatedWikiFreqs, setAggregatedWikiFreqs] = useState([]);
  const [infoBoxDimensions, setInfoBoxDimensions] = useState({ width: 0, height: 0 });
  const infoBoxRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [autoRotate, setAutoRotate] = useState(true);
  const [loadingMsg, setLoadingMsg] = useState("");
  const [endMsg, setEndMsg] = useState("");
  const [isTitleCollapsed, setIsTitleCollapsed] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('Controls');

  const [textTitle, setTextTitle] = useState("");
  const [textAuthor, setTextAuthor] = useState("");
  const [lyricsText, setLyricsText] = useState("");
  const [diyError, setDiyError] = useState("");
  const [diyUploading, setDiyUploading] = useState(false);
  const wordCount = lyricsText.trim().split(/\s+/).filter(word => word.length > 0).length;

  const { width, height, dwidth, dheight } = useWindowSize();
  const isMobile = window.matchMedia('(pointer: coarse)').matches;

  const titleRef = useRef();
  const menuButtonRef = useRef();
  const [centerY, setCenterY] = useState(null);

  const [resetCameraTrigger, setResetCameraTrigger] = useState(0);

  // To reset camera
  const controlsRef = useRef();
  // Rotating group ref
  const groupRef = useRef();

  // Updated handleReset function
  function handleReset() {
    setAutoRotate(true);
    setResetCameraTrigger(prev => prev + 1); // Trigger reset by changing value
  }

  // Callback when reset is complete
  const handleResetComplete = useCallback(() => {
    // Optional: any cleanup after reset
  }, []);

  // Helper to convert 3D world position to screen coordinates
  const worldToScreen = useCallback((worldPosition, camera, renderer) => {
    const vector = new THREE.Vector3(...worldPosition);
    vector.project(camera);
    
    let x = (vector.x * 0.5 + 0.5) * renderer.domElement.clientWidth;
    let y = (vector.y * -0.5 + 0.5) * renderer.domElement.clientHeight;
    
    // Keep info box within screen bounds
    const boxWidth = infoBoxDimensions.width || 200; // fallback to minimum width
    const boxHeight = infoBoxDimensions.height || 50; // fallback to minimum height
    const margin = 10; // Margin we want for all sides of clipping
    const topOffset = width < 900 ? 15 : 25; // Factoring in how high the info box is above the word

    // Account for transform: translate(-50%, -100%)
    // This means the box's actual left edge is at: x - boxWidth/2
    // And the box's actual top edge is at: y - boxHeight - topOffset
    
    // Calculate bounds using available dimensions
    const boxLeft = x - boxWidth/2;
    const boxRight = x + boxWidth/2;
    const boxTop = y - boxHeight - topOffset;
    const boxBottom = y - topOffset;
    
    const minX = margin;
    const maxX = dwidth - margin;   // Use safe-area-aware dimensions for boundary-setting
    const minY = margin;
    const maxY = dheight - margin;
    
    // Clamp to bounds
    if (boxLeft < minX) {
      x = minX + boxWidth/2;
    } else if (boxRight > maxX) {
      x = maxX - boxWidth/2;
    }
    
    if (boxTop < minY) {
      y = minY + boxHeight + topOffset;
    } else if (boxBottom > maxY) {
      y = maxY + topOffset;
    }
    
    return { x, y };
  }, [infoBoxDimensions, dwidth, dheight, width]);

  // Aggregate all songs' data for visualization
  useEffect(() => {
    // Group words by their text to find shared words
    const wordInstancesMap = new Map();
    
    songs.forEach(song => {
      const songWords = song.words.map(w => ({
        ...w,
        color: song.masterColor || song.sectionColors?.[w.section] || w.color,
        songId: song.id
      }));
      
      songWords.forEach(wordData => {
        const key = wordData.word.toLowerCase(); // Case-insensitive grouping
        if (!wordInstancesMap.has(key)) {
          wordInstancesMap.set(key, []);
        }
        wordInstancesMap.get(key).push(wordData);
      });
    });

    // Convert to arrays for rendering, keeping all color instances
    let allWords = [];
    let allPositions = [];
    let allColorInstances = []; // Array of color arrays
    let allSections = [];
    let allRadii = [];
    let allWikiFreqs = [];

    wordInstancesMap.forEach((instances, word) => {
      // Use the first instance for position, radius, etc.
      const primary = instances[0];
      
      // Collect all unique colors for this word (filter out empty/transparent)
      const colors = instances
        .map(inst => inst.color)
        .filter(color => color && color !== "" && color !== "transparent");
      
      // If all instances are transparent, keep one empty entry
      const finalColors = colors.length > 0 ? colors : [""];
      
      allWords.push(word);
      allPositions.push(primary.position);
      allColorInstances.push(finalColors);
      allSections.push(primary.section);
      allRadii.push(primary.radius);
      allWikiFreqs.push(primary.wikiFreq);
    });

    setAggregatedWords(allWords);
    setAggregatedPositions(allPositions);
    setAggregatedColors(allColorInstances); // Now array of arrays
    setAggregatedSections(allSections);
    setAggregatedRadii(allRadii);
    setAggregatedWikiFreqs(allWikiFreqs);
  }, [songs]); // Keep the full songs dependency since we need all the data

  // Update info box dimensions when content changes
  useEffect(() => {
    if (infoBoxRef.current && selectedWordInfo) {
      // Use requestAnimationFrame to ensure the DOM is updated
      requestAnimationFrame(() => {
        if (infoBoxRef.current) {
          const rect = infoBoxRef.current.getBoundingClientRect();
          setInfoBoxDimensions({
            width: rect.width,
            height: rect.height
          });
        }
      });
    }
  }, [selectedWordInfo]); // Re-measure when word info changes

  // Clear selected word info when aggregated data changes
  useEffect(() => {
    setSelectedWordInfo(null);
  }, [aggregatedWords, aggregatedPositions, aggregatedColors]);

  // --- Logic after user presses Add button ---
  async function handleAddSong() {
    setLoading(true);
    setLoadingMsg("Starting lyric load...");

    if (!selectedSongId) {
      /* setLoadingMsg("Clearing canvas...");
      const stickSeasonAlbum = {
        json: "/MusicViz/stick_season_wordvecs.json",
        name: "Noah Kahan - Stick Season"
      };
      await loadSong(stickSeasonAlbum);
      setLoading(false); */
      setEndMsg("Please search and select a song from the dropdown.")
      setLoading(false);
      return;
    }

    try {
      setLoadingMsg("Loading lyrics...");
      const lyricsResp = await fetch('https://witty-moccasin-fully.ngrok-free.app/api/get-lyrics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ songId: selectedSongId }),
      });

      const lyricsData = await lyricsResp.json();

      if (lyricsData.status?.length) {
        const lastStatus = lyricsData.status[lyricsData.status.length - 1];
        setLoadingMsg(lastStatus);

        if ('error' in lyricsData || !lyricsData.vectors || Object.keys(lyricsData.vectors).length === 0) {
          setEndMsg(`${lastStatus || 'Unknown error.'}`);
          setLoading(false);
          return;
        }
      } else {
        setEndMsg("");
      }

      const sectionVectors = lyricsData.vectors;
      if (!sectionVectors || Object.keys(sectionVectors).length === 0) {
        setLoading(false);
        return;
      }

      const genre = lyricsData.genre || []; // Extract genre array
      const label = lyricsData.label || null; // Extract label

      setLoadingMsg("Building word vector list...");
      let wordVectorList = [];
      let sectionNames = Object.keys(sectionVectors);
      let sectionColorMap = {};
      const defaultSongColor = DEFAULT_SECTION_COLORS[songs.length % DEFAULT_SECTION_COLORS.length]; // One color per song
      sectionNames.forEach(section => {
        sectionColorMap[section] = defaultSongColor;
        // UPDATED: Extract data from the word object
        Object.entries(sectionVectors[section]).forEach(([word, wordData]) => {
          wordVectorList.push({
            word,
            section,
            vector: wordData.vector,           // Extract vector array
            color: defaultSongColor,
            sectionFreq: wordData.section_freq, // Extract section frequency
            songFreq: wordData.song_freq,       // Extract song frequency
            wikiFreq: wordData.wiki_freq,       // Extract wiki frequency
            radius: wordData.radius             // Pre-calculated radius
          });
        });
      });

      setLoadingMsg("Computing vector positions...");
      const SPREAD = 15;
      const positions = wordVectorList.map(wv => 
        wv.vector.map(coord => coord * SPREAD)
      );

      const visualizedWords = wordVectorList.map((wv, idx) => ({
        word: wv.word,
        section: wv.section,
        color: wv.color,
        position: positions[idx],
        sectionFreq: wv.sectionFreq,
        songFreq: wv.songFreq,
        wikiFreq: wv.wikiFreq,
        radius: wv.radius 
      }));

      setLoadingMsg("Loading song metadata...");
      const metaResp = await fetch(`/.netlify/functions/getMeta?id=${selectedSongId}`);
      const metaJson = await metaResp.json();

      const songObj = {
        words: visualizedWords,
        name: metaJson.response.song.title_with_featured ||
          metaJson.response.song.full_title ||
          metaJson.response.song.title,
        artist: metaJson.response.song.primary_artist_names ||
          metaJson.response.song.artist_names ||
          "Unknown Artist",
        language: metaJson.response.song.language || "n/a",
        release_date: metaJson.response.song.release_date || "n/a"
      };

      setLoadingMsg("Rendering on canvas...");
      const sections = {};
      sectionNames.forEach(s => {
        sections[s] = true;
      });

      const loadedSong = {
        id: selectedSongId,
        name: songObj.name,
        artist: songObj.artist,
        language: songObj.language,
        release_date: songObj.release_date,
        sections,
        sectionColors: sectionColorMap,
        words: visualizedWords,
        word_count: wordVectorList.length, // Number of words visualized
        masterColor: "", // Default empty per song
        genre: genre, // Array of genres
        label: label, // Record label
      };

      setSongs(currentSongs => {
        const filtered = currentSongs.filter(s => s.id !== selectedSongId);
        const newSongs = [...filtered, loadedSong];
        if (currentSongs.length === 0 && isDashboardCollapsed && firstTime) {
          setIsDashboardCollapsed(false); // Auto-open on first add
          setFirstTime(false);
        }
        setNewlyAddedSongId(loadedSong.id);  // Trigger auto-expand
        return newSongs;
      });

      setLoadingMsg("");
      setLoading(false);
      setEndMsg(`Words Visualized: ${wordVectorList.length}`);
    } catch (err) {
      console.error("Error caught: ", err);
      const errorMsg = String(err);
      let displayMsg = "Loading error. Please try another song.";
      if (errorMsg.includes("TypeError: Failed to fetch")) {
        displayMsg = "Server maintenance. Try again in a few hours!";
      }
      setLoadingMsg(displayMsg);
      setEndMsg(displayMsg);
      setLoading(false);
    }
  }

  // --- Logic after user presses Clear button ---
  const handleClearDIY = () => {
    setTextTitle("");
    setTextAuthor("");
    setLyricsText("");
  };

  // Check if any field has content for button color
  const hasContent = textTitle.trim() || textAuthor.trim() || lyricsText.trim();

  // --- Logic after user presses Upload button ---
  async function handleDIYUpload() {
    // Clear previous errors
    setDiyError("");
    
    // Check word count
    if (wordCount > 2000) {
      setDiyError("Text exceeds 2000 word limit. Please reduce the length.");
      return;
    }
    
    if (lyricsText.trim().length === 0) {
      setDiyError("Please enter some lyrics before uploading.");
      return;
    }
    
    setDiyUploading(true);
    
    try {
      const response = await fetch('https://witty-moccasin-fully.ngrok-free.app/api/process-lyrics-text', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true'
        },
        body: JSON.stringify({
          text: lyricsText,
          title: textTitle || "Custom Text",
          author: textAuthor || "Unknown Author"
        })
      });

      let errorText = "";
      let data = null;
      try {
        data = await response.json();
      } catch (jsonErr) {
        errorText = "Failed to parse response.";
      }

      if (!response.ok) {
        // Prefer API error field if present
        setDiyError(data?.error || errorText || `Upload failed with status ${response.status}`);
        setDiyUploading(false);
        return;
      }
      
      // Process the response similar to how you handle song data
      if (data.vectors && Object.keys(data.vectors).length > 0) {
        const sectionVectors = data.vectors;
        let wordVectorList = [];
        let sectionNames = Object.keys(sectionVectors);
        let sectionColorMap = {};
        const defaultSongColor = DEFAULT_SECTION_COLORS[songs.length % DEFAULT_SECTION_COLORS.length];
        
        sectionNames.forEach(section => {
          sectionColorMap[section] = defaultSongColor;
          Object.entries(sectionVectors[section]).forEach(([word, wordData]) => {
            wordVectorList.push({
              word,
              section,
              vector: wordData.vector,
              color: defaultSongColor,
              sectionFreq: wordData.section_freq,
              songFreq: wordData.song_freq,
              wikiFreq: wordData.wiki_freq,
              radius: wordData.radius
            });
          });
        });
        
        const SPREAD = 15;
        const positions = wordVectorList.map(wv => 
          wv.vector.map(coord => coord * SPREAD)
        );
        
        const visualizedWords = wordVectorList.map((wv, idx) => ({
          word: wv.word,
          section: wv.section,
          color: wv.color,
          position: positions[idx],
          sectionFreq: wv.sectionFreq,
          songFreq: wv.songFreq,
          wikiFreq: wv.wikiFreq,
          radius: wv.radius
        }));
        
        const sections = {};
        sectionNames.forEach(s => {
          sections[s] = true;
        });
        
        const loadedSong = {
          id: `diy-${Date.now()}`, // Generate unique ID for DIY uploads
          name: textTitle || "Custom Text",
          artist: textAuthor || "Unknown Author",
          language: "n/a",
          sections,
          sectionColors: sectionColorMap,
          words: visualizedWords,
          word_count: wordVectorList.length,
          masterColor: "",
          genre: ["DIY Upload"],
        };
        
        setSongs(currentSongs => {
          const newSongs = [...currentSongs, loadedSong];
          if (currentSongs.length === 0 && isDashboardCollapsed && firstTime) {
            setIsDashboardCollapsed(false);
            setFirstTime(false);
          }
          setNewlyAddedSongId(loadedSong.id);
          return newSongs;
        });
        
        // Close menu and populate EndMsg on success
        setIsMenuOpen(false); // Close the menu
        setEndMsg(`Words Visualized: ${wordVectorList.length}`);
        
      } else {
        setDiyError("Failed to process lyrics. Please try again.");
      }
      
    } catch (error) {
      console.error("DIY upload error:", error);
      setDiyError(error.message || "Upload failed. Please check your connection and try again.");
    } finally {
      setDiyUploading(false);
    }
  }

  // Recalculation of heights and widths after initial mount
  useEffect(() => {
    // Force a recalculation after initial mount
    const forceUpdate = setTimeout(() => {
      const event = new Event('resize');
      window.dispatchEvent(event);
    }, 100);

    return () => clearTimeout(forceUpdate);
  }, []); // Empty dependency array - runs once after mount

  // Handling orientation changes
  useEffect(() => {
    const handleOrientationChange = () => {
      // Force a re-render after orientation change
      setTimeout(() => {
        const event = new Event('resize');
        window.dispatchEvent(event);
      }, 100);
    };

    window.addEventListener('orientationchange', handleOrientationChange);
    return () => window.removeEventListener('orientationchange', handleOrientationChange);
  }, []);

  // Update center between elements vertically
  useEffect(() => {
  function updateCenterY() {
      if (titleRef.current && menuButtonRef.current) {
      const titleRect = titleRef.current.getBoundingClientRect();
      const menuRect = menuButtonRef.current.getBoundingClientRect();
      const mid = (titleRect.bottom + menuRect.top) / 2;
      setCenterY(mid >= 50 ? mid : -1);
      }
  }
  updateCenterY();
  window.addEventListener('resize', updateCenterY);
  return () => window.removeEventListener('resize', updateCenterY);
  }, [isTitleCollapsed, isDashboardCollapsed, width, height]);

  // Menu handlers
  const handleMenuToggle = () => {
      setIsMenuOpen(!isMenuOpen);
  };

  const handleTabClick = (tab) => {
      setActiveTab(tab);
  };

  const handleMenuOutsideClick = (e) => {
  if (e.target.classList.contains('menu-overlay')) {
      setIsMenuOpen(false);
  }
  };

  // Word click handler
  const handleWordClick = (event, wordInfo) => {
    // Clear first to force a clean state transition
    setSelectedWordInfo(null);
    
    // Calculate song occurrences with colors
    const songOccurrences = [];
    
    songs.forEach(song => {
      const sectionsData = [];
      
      // Get the original section order from the song
      const sectionOrder = Object.keys(song.sections || {});
      
      song.words
        .filter(w => w.word.toLowerCase() === wordInfo.word.toLowerCase())
        .forEach(w => {
          // Get the color for this section
          const sectionColor = song.masterColor || song.sectionColors?.[w.section] || "";
          
          // Find if we already have this section
          const existingSection = sectionsData.find(s => s.name === w.section);
          if (!existingSection) {
            sectionsData.push({
              name: w.section,
              color: sectionColor
            });
          }
        });
      
      if (sectionsData.length > 0) {
        // Sort sections by their original order in the song 
          // For alphabetical order instead, do `sectionsData.sort((a, b) => a.name.localeCompare(b.name));`
        sectionsData.sort((a, b) => {
          const indexA = sectionOrder.indexOf(a.name);
          const indexB = sectionOrder.indexOf(b.name);
          return indexA - indexB;
        });
        
        songOccurrences.push({
          songName: song.name,
          sections: sectionsData
        });
      }
    });
    
    // Use setTimeout to ensure the clear happens before setting new info
    setTimeout(() => {
      setSelectedWordInfo({
        ...wordInfo,
        songOccurrences
      });
    }, 0);
  };

  // Canvas background click handler
  const handleCanvasClick = (event) => {
    // In React Three Fiber, clicking on the canvas background will have event.object undefined
    // or event.object will be null when clicking empty space
    if (event.object) {
      // Something was clicked (could be a word), don't clear selection
      return;
    }
    // Clear selection if canvas was clicked
    setSelectedWordInfo(null);
  };

  // Scroll to top on first navigation or refresh
  /* useEffect(() => {
      // Temporarily re-allow scrolling (helps on iOS/Safari)
      const originalOverflow = document.body.style.overflow;
      const originalTouchAction = document.body.style.touchAction;
      document.body.style.overflow = "auto";
      document.body.style.touchAction = "auto";
  
      // Next animation frame, scroll to top, then restore overflow lock
      requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
  
      // Lock scroll again AFTER resetting position
      document.body.style.overflow = originalOverflow;
      document.body.style.touchAction = originalTouchAction;
      });
  }, []); */

  // Load album on first mount and when dropdown changes - I don't want this, only when the user presses Add
  /* useEffect(() => {
      loadSong();
      // eslint-disable-next-line
  }, [selectedAlbum]); */

  // Allow for double-tap on mobile
  useDoubleTap(handleReset, 350, 'orbit-canvas');

  // Handle user drag interaction: disables auto-rotation, shows reset icon
  function handleDragStart() {
      setAutoRotate(false);
      //setShowReset(true);
  }

  // Screen position tracker component
  function ScreenPositionTracker({ selectedWordInfo, onPositionUpdate }) {
    const { camera, gl } = useThree();
    
    useFrame(() => {
      if (selectedWordInfo && selectedWordInfo.position) {
        const screenPos = worldToScreen(selectedWordInfo.position, camera, gl);
        onPositionUpdate(screenPos);
      }
    });
    
    return null;
  }

  return (
      <div
        style={{
          position: "fixed",
          top: "env(safe-area-inset-top, 0px)",
          bottom: "env(safe-area-inset-bottom, 0px)",
          left: "env(safe-area-inset-left, 0px)",
          right: "env(safe-area-inset-right, 0px)",
          width: "auto",
          height: "auto",
          minWidth: 0,
          minHeight: 0,
          background: "rgba(0,0,0,0.75)",
          overflow: "hidden",
          boxSizing: "border-box",
          userSelect: "none",
          WebkitUserSelect: "none",
          msUserSelect: "none",
          MozUserSelect: "none",
        }}
      >
          {/* Top Bar: Title, Dropdown, Add Button */}
          <div
          ref={titleRef}
          style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              position: 'absolute',
              top: `calc(20px + env(safe-area-inset-top, 0px))`,
              left: 20,
              zIndex: 10,
              padding: isTitleCollapsed ? 0 : 12,
              background: "rgba(0,0,0,0.8)",
              color: '#333',
              border: isTitleCollapsed ? 'none' : '2px solid #ddd',
              borderRadius: '8px',
              fontFamily: "'Sniglet', sans-serif",
              fontWeight: 400,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              width: isTitleCollapsed 
                ? '60px' 
                : dwidth < 450 
                  ? `${Math.max(Math.min(dwidth, window.innerWidth) - 20 - 12 - 2 - 2 - 12 - 20, 200)}px`  // Account for left margin (20) + padding (12*2) + borders (2*2) + safety margin (20)
                  : 'unset',
              height: isTitleCollapsed ? '60px' : 'unset',
              // Smooth transition for specific properties only
              transition: 'width 0.1s cubic-bezier(.4,1,.3,1), height 0.1s cubic-bezier(.4,1,.3,1), padding 0.1s cubic-bezier(.4,1..3,1), border 0.1s cubic-bezier(.4,1,.3,1)',
          }}
          >
          {isTitleCollapsed ? (
              <button
              onClick={() => setIsTitleCollapsed(false)}
              title="Expand"
              style={{
                  background: "linear-gradient(90deg,#89CFF0,#69b3e7)",
                  border: "none",
                  borderRadius: "8px",
                  fontFamily: "'Sniglet', sans-serif",
                  fontSize: "60px",
                  color: "#333",
                  width: "60px",
                  height: "60px",
                  margin: "0 auto",
                  fontWeight: 800,
                  cursor: "pointer",
                  boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                  transition: "transform 0.15s",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
              }}
              // For mouse (desktop)
              onMouseEnter={e => e.currentTarget.style.transform = "scale(1.08)"}
              onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
              // For touch (mobile)
              onTouchStart={e => {
                e.currentTarget.style.transform = "scale(1.08)";
                e.currentTarget.classList.add('touch-active');
              }}
              onTouchEnd={e => {
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.classList.remove('touch-active');
              }}
              onTouchCancel={e => {
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.classList.remove('touch-active');
              }}
              >
              L
              </button>
          ) : (
              <>
              <span
                  style={{
                  fontFamily: "'Sniglet', sans-serif",
                  fontWeight: 800,
                  fontSize: Math.min(
                    width < 375 ? (width * 0.082) : (width < 900 ? (width * 0.086) : 36),
                    36  // Cap at 36px maximum
                  ),
                  color: 'white',
                  margin: '3px 12px 12px 12px',
                  letterSpacing: "0.03em",
                  }}
              >
                  Lyric Explorer
              </span>
              <div
                  //onSubmit={e => { e.preventDefault(); loadSong(); }}
                  style={{
                    display: "flex",
                    gap: 10,
                    alignItems: "flex-start",
                    marginTop: 12,
                    marginLeft: 3,
                    marginRight: 3
                  }}>
                  <SongSearch
                    selectedSongId={selectedSongId}
                    setSelectedSongId={setSelectedSongId}
                    query={searchQuery}  // Pass as props
                    setQuery={setSearchQuery}
                    selectedSongText={selectedSongText}
                    setSelectedSongText={setSelectedSongText}
                  />
                  <button
                    onClick={e => {
                      e.preventDefault(); // Not strictly necessary, but safe
                      handleAddSong();
                    }}
                    title="Add Song to Canvas"
                    style={{
                      marginLeft: 8,
                      background: REG_BLUE,
                      color: "black",
                      borderRadius: 12,
                      border: "1px solid #348FD9",
                      width: 60,
                      height: 30,
                      fontFamily: "'Sniglet', sans-serif",
                      fontSize: 16,
                      fontWeight: 400,
                      cursor: 'pointer',
                    }}>
                  Add
                  </button>
              </div>

              {/* Loading message */}
              {loading && (
                <div
                  style={{
                    padding: "24px 0 9px 14px",
                    color: REG_BLUE,
                    fontSize: 13,
                    background: "transparent",
                    textAlign: "left",
                  }}
                >
                  {loadingMsg || "Loading..."}
                </div>
              )}
              {!loading && (
                <div
                  style={{
                    width: "87.5%",
                    margin: "24px 0px 9px 14px",
                    fontSize: 13,
                    color: REG_BLUE,
                    whiteSpace: "pre-wrap",
                    left: 0,
                  }}
                >
                  {endMsg}
                </div>
              )}

              {/* Minimize button absolutely positioned flush to bottom right */}
              <span
                  onClick={() => setIsTitleCollapsed(true)}
                  style={{
                  position: "absolute",
                  right: "6px",
                  bottom: "6px",
                  background: "rgba(50,50,50,0.85)",
                  border: "none",
                  borderRadius: "7px",
                  padding: "0px 8px",
                  fontSize: "18px",
                  fontFamily: 'Sniglet',
                  fontWeight: 800,
                  color: REG_BLUE,
                  cursor: "pointer",
                  WebkitTapHighlightColor: "transparent",
                  transition: "background 0.18s, color 0.15s, box-shadow 0.16s",
                  boxShadow: "none",
                  }}
                  // For mouse (desktop)
                  onMouseEnter={e => {
                  e.currentTarget.style.background = "#444";
                  e.currentTarget.style.color = "#fff";
                  }}
                  onMouseLeave={e => {
                  e.currentTarget.style.background = "rgba(50,50,50,0.85)";
                  e.currentTarget.style.color = REG_BLUE;
                  }}
                  // For touch (mobile)
                  onTouchStart={e => {
                    e.currentTarget.style.background = "#444";
                    e.currentTarget.style.color = "#fff";
                    e.currentTarget.classList.add('touch-active');
                  }}
                  onTouchEnd={e => {
                    e.currentTarget.style.background = "rgba(50,50,50,0.85)";
                    e.currentTarget.style.color = REG_BLUE;
                    e.currentTarget.classList.remove('touch-active');
                  }}
                  onTouchCancel={e => {
                    e.currentTarget.style.background = "rgba(50,50,50,0.85)";
                    e.currentTarget.style.color = REG_BLUE;
                    e.currentTarget.classList.remove('touch-active');
                  }}
                  title="Collapse"
              >
                  ⇱
              </span>
              </>
          )}
          </div>

          {/* Button Row - Bottom Left Corner */}
          <div
            style={{
              position: 'absolute',
              bottom: width < 900 && !isDashboardCollapsed 
                ? 'calc(20px + 40vh)' 
                : '20px',
              left: 20,
              zIndex: 9,
              display: 'flex',
              gap: '15px', // spacing between buttons
              alignItems: 'center', // Ensures same height alignment
            }}
          >
            {/* Info Button */}
            <button
              ref={menuButtonRef}
              title="More Information"
              onClick={(e) => {
                e.stopPropagation();
                handleMenuToggle();
              }}
              style={{
                fontSize: '18px',
                padding: '12px 16px',
                backgroundColor: 'rgba(0,0,0, 0.8)',
                color: 'white',
                border: '1.5px solid #ddd',
                borderRadius: '8px',
                cursor: 'pointer',
                fontFamily: "'Sniglet', sans-serif",
                fontWeight: 400,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                height: 'auto', // Let content determine height
                minHeight: '44px', // Ensure minimum touch-friendly height
              }}
            >
              ☰ Menu
            </button>

            {/* Share Button */}
            <button
              title="Share"
              onClick={(e) => {
                e.stopPropagation();
                
                // Share functionality
                const shareData = {
                  text: `Check out Lyric Explorer!`,
                  url: 'https://reilly.fun/lyrics'
                };
                
                if (navigator.share) {
                  navigator.share(shareData).catch((error) => {
                    if (error.name !== 'AbortError') {
                      console.log('Share failed:', error);
                    }
                  });
                } else {
                  // Fallback for browsers that don't support Web Share API
                  console.log('Sharing not supported on this device');
                }
              }}
              style={{
                fontSize: '18px',
                padding: '12px 16px',
                backgroundColor: 'rgba(0,0,0, 0.8)',
                color: 'white',
                border: '1.5px solid #ddd',
                borderRadius: '8px',
                cursor: 'pointer',
                fontFamily: "'Sniglet', sans-serif",
                fontWeight: 400,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                height: 'auto',
                minHeight: '44px',
              }}
            >
              <span style={{ display: 'inline-block', transform: 'rotate(90deg)', verticalAlign: 'middle' }}>
                ⎋
              </span>
              {' '}&nbsp;Share
            </button>

            {/* Coffee Button 
            <button
              title="Coffee"
              onClick={(e) => {
                e.stopPropagation();
                window.open('https://buymeacoffee.com/reillyfun', '_blank');
              }}
              style={{
                fontSize: '18px',
                padding: '12px 16px',
                backgroundColor: 'rgba(0,0,0, 0.8)',
                color: 'white',
                border: '1.5px solid #ddd',
                borderRadius: '8px',
                cursor: 'pointer',
                fontFamily: "'Sniglet', sans-serif",
                fontWeight: 400,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                height: 'auto',
                minHeight: '44px',
              }}
            >
              ☕&nbsp;&nbsp;Coffee
            </button> */}
          </div>

            {/* Menu overlay rendered to document.body using portal */}
            {isMenuOpen && createPortal(
              <div 
              className="menu-overlay"
              onClick={handleMenuOutsideClick}
              style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  width: width,
                  height: height,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
              }}
              >
                <div style={{
                    backgroundColor: DEEP_BLUE,
                    opacity: 0.95,
                    borderRadius: '12px',
                    minWidth: width * 0.2,
                    width: 'auto',
                    maxWidth: width * 0.9,
                    maxHeight: 'calc(90vh - 100px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
                    position: 'relative',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                    overflow: 'hidden', // Prevents visible negative space in corners
                    display: 'flex',
                    flexDirection: 'column',
                    //paddingBottom: '24px',
                }}>
                  {/* Header with tabs and close button */}
                  <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 20px 0 20px',
                  borderBottom: '2px solid #555555'
                  }}>
                  <div style={{
                      display: 'flex',
                      gap: '0px',
                      fontFamily: "'Sniglet', sans-serif",
                      fontSize: width < 900 ? Math.min(18, width * 0.03) : 18,
                  }}>
                      {['Controls', 'About', 'DIY'].map((tab, index) => (
                      <React.Fragment key={tab}>
                          <span
                          onClick={() => handleTabClick(tab)}
                          style={{
                              cursor: 'pointer',
                              color: activeTab === tab ? 'white' : '#a9a9a9',
                              fontWeight: 400,
                              transition: 'color 0.2s ease',
                              WebkitTapHighlightColor: 'transparent',
                          }}
                          // For mouse (desktop)
                          onMouseEnter={(e) => e.target.style.color = 'white'}
                          onMouseLeave={(e) => e.target.style.color = activeTab === tab ? 'white' : '#a9a9a9'}
                          // For touch (mobile)
                          onTouchStart={e => {
                            e.target.style.color = 'white';
                            e.currentTarget.classList.add('touch-active');
                          }}
                          onTouchEnd={e => {
                            e.target.style.color = activeTab === tab ? 'white' : '#a9a9a9';
                            e.currentTarget.classList.remove('touch-active');
                          }}
                          onTouchCancel={e => {
                            e.target.style.color = activeTab === tab ? 'white' : '#a9a9a9';
                            e.currentTarget.classList.remove('touch-active');
                          }}
                          >
                          {tab}
                          </span>
                          {index < 3 && <span style={{ color: '#a9a9a9', margin: '0 12px' }}>|</span>}
                      </React.Fragment>
                      ))}
                  </div>
                  <span
                      onClick={() => setIsMenuOpen(false)}
                      style={{
                      fontSize: '24px',
                      cursor: 'pointer',
                      color: '#a9a9a9',
                      transition: 'color 0.2s ease',
                      padding: '4px',
                      marginLeft: '32px',
                      WebkitTapHighlightColor: 'transparent',
                      }}
                      // For mouse (desktop)
                      onMouseEnter={(e) => e.target.style.color = 'white'}
                      onMouseLeave={(e) => e.target.style.color = '#a9a9a9'}
                      // For touch (mobile)
                      onTouchStart={e => {
                        e.target.style.color = 'white';
                        e.currentTarget.classList.add('touch-active');
                      }}
                      onTouchEnd={e => {
                        e.target.style.color = '#a9a9a9';
                        e.currentTarget.classList.remove('touch-active');
                      }}
                      onTouchCancel={e => {
                        e.target.style.color = '#a9a9a9';
                        e.currentTarget.classList.remove('touch-active');
                      }}
                  >
                      ×
                  </span>
                  </div>

                  {/* Tab Content */}
                  <div
                  id="tab-content"
                  style={{
                    padding: '20px',
                    maxHeight: 'calc(90vh - 100px)',
                    overflowY: 'auto'
                  }}>
                  {activeTab === 'Controls' && (
                    <div
                      id="controls-section"
                      style={{
                        fontFamily: "'Sniglet', sans-serif",
                        fontWeight: 400,
                      }}>
                      {!isMobile ? (
                          <div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '0.75px solid #a9a9a9' }}>
                                  <span style={{ color: 'white' }}>Left click + drag</span>
                                  <span style={{ color: '#a9a9a9' }}>Rotate</span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '0.75px solid #a9a9a9' }}>
                                  <span style={{ color: 'white' }}>Scroll wheel</span>
                                  <span style={{ color: '#a9a9a9' }}>Zoom</span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '0.75px solid #a9a9a9' }}>
                                  <span style={{ color: 'white' }}>Right click + drag</span>
                                  <span style={{ color: '#a9a9a9' }}>Pan</span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '0.75px solid #a9a9a9' }}>
                                  <span style={{ color: 'white' }}>Double click</span>
                                  <span style={{ color: '#a9a9a9' }}>Reset zoom</span>
                              </div>
                          </div>
                      ) : (
                          <div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '0.75px solid #a9a9a9' }}>
                                  <span style={{ color: 'white' }}>Swipe</span>
                                  <span style={{ color: '#a9a9a9' }}>Rotate</span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '0.75px solid #a9a9a9' }}>
                                  <span style={{ color: 'white' }}>Pinch</span>
                                  <span style={{ color: '#a9a9a9' }}>Zoom</span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '0.75px solid #a9a9a9' }}>
                                  <span style={{ color: 'white' }}>Two-finger swipe</span>
                                  <span style={{ color: '#a9a9a9' }}>Pan</span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '0.75px solid #a9a9a9' }}>
                                  <span style={{ color: 'white' }}>Double tap</span>
                                  <span style={{ color: '#a9a9a9' }}>Reset zoom</span>
                              </div>
                          </div>
                      )}
                      <button
                          onClick={() => navigate('/')}
                          style={{
                          fontSize: '16px',
                          fontFamily: "'Sniglet', sans-serif",
                          fontWeight: 400,
                          padding: '10px 20px',
                          backgroundColor: QUIT_RED,
                          color: 'white',
                          border: 'none',
                          borderRadius: '10px',
                          cursor: 'pointer',
                          width: 'fit-content',
                          margin: '20px auto 0 auto',
                          display: 'block',
                          }}
                      >
                          Quit
                      </button>
                      </div>
                  )}

                  {activeTab === 'About' && (
                      <div 
                      id="about-section"
                      style={{
                        fontFamily: "'Sniglet', sans-serif",
                        fontSize: '16px',
                        lineHeight: '1.6',
                        whiteSpace: 'normal',
                        maxWidth: '520px',
                        padding: "10px 20px 20px 20px",
                        overflowY: 'visible',
                        color: 'white',
                      }}>
                      <p>
                          Hi, I'm Reilly! Welcome to Lyric Explorer.
                          <br />
                          <br />
                          Lyric Explorer allows you to visualize song lyrics in the form of a word map.
                          The closer two words are in the map, the closer they are in meaning. The size
                          of each word has been scaled to its frequency in Wikipedia pages – the larger
                          a word appears, the rarer it is. This makes the interesting words stand out.
                          <br />
                          <br />
                          You can go anywhere in the map you'd like! Use the Dashboard to change the colors
                          of the words in a song or song section. The Dashboard also has stats on each song.
                          Click on a word in the map to see how many songs / song sections share that word.
                          <br />
                          <br />
                          The idea for Lyric Explorer sprouted alongside my obsession with Noah Kahan's
                          <i> Stick Season </i> album. I came for his self-deprecating humor and stayed for
                          his depictions of remote, cozy villages. But like many, I wanted more. So I made
                          Lyric Explorer to search for themes in the words he sang, hoping that they might
                          get me closer to the essence of what defines the small-town suburbia experience.
                          (me? crazy? nahhh no way) 🙃
                          <br />
                          <br />
                          Under the hood, this project uses word vectorization, which quantifies the
                          meaning of words by ranking where they stand on hundreds of different dimensions.
                          Here, I'm using a dataset of word vectors pre-trained on{' '}
                          <a 
                          href="https://commoncrawl.org/" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ color: "#69b3e7" }}
                          >
                          Common Crawl
                          </a>, a free and open
                          repository of web crawl data. These 2.2 million words and their vectors were
                          trained with the{' '}
                          <a 
                          href="https://github.com/stanfordnlp/glove" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ color: "#69b3e7" }}
                          >
                          GloVe
                          </a> algorithm, which creates a global map of word vectors
                          suitable for visualizing multiple songs at the same time. PCA compression
                          was also used to reduce high dimensionality (300-D) into something more
                          friendly to our eyes (3-D).
                          <br />
                          <br />
                          Hope you enjoy! Let me know what you think and if there's anything else you'd
                          like to see.
                          <br />
                          <br />For more little creations, check out{' '}
                          <a 
                          href="https://reilly.fun" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ color: "#69b3e7", fontFamily: "'Fredoka One', cursive" }}
                          >
                          reilly.fun
                          </a> 🤠
                      </p>

                  </div>
                  )}

                  {activeTab === 'DIY' && (
                    <div style={{
                      fontFamily: "'Sniglet', sans-serif",
                      color: 'white',
                      fontSize: '14px',
                      padding: '20px',
                      minWidth: dwidth < 450 ? '300px' : '520px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '20px'
                    }}>
                      {/* Title and Author inputs */}
                      <div style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
                        <input
                          type="text"
                          placeholder="Text title"
                          value={textTitle}
                          onChange={(e) => setTextTitle(e.target.value)}
                          style={{
                            padding: '10px',
                            borderRadius: '6px',
                            border: '1px solid #555',
                            backgroundColor: '#2a3441',
                            color: 'white',
                            fontFamily: "'Sniglet', sans-serif",
                            fontSize: '14px'
                          }}
                        />
                        <input
                          type="text"
                          placeholder="Text author"
                          value={textAuthor}
                          onChange={(e) => setTextAuthor(e.target.value)}
                          style={{
                            padding: '10px',
                            borderRadius: '6px',
                            border: '1px solid #555',
                            backgroundColor: '#2a3441',
                            color: 'white',
                            fontFamily: "'Sniglet', sans-serif",
                            fontSize: '14px'
                          }}
                        />
                      </div>

                      {/* Main textarea with placeholder */}
                      <div style={{ position: 'relative' }}>
                        <textarea
                          placeholder="[Verse 1]&#13;&#10;
                            Upload your own lyrics here!&#13;&#10;
                            You can use the words from any song,&#13;&#10;
                            poem, book, or other body of text.&#13;&#10;
                            (Must be 2,000 words or less.)&#13;&#10;
                            &#8203;&#13;&#10;
                            [Verse 2]&#13;&#10;
                            Use brackets for section titles,&#13;&#10;
                            as shown here. Happy visualizing!"
                          value={lyricsText}
                          onChange={(e) => setLyricsText(e.target.value)}
                          style={{
                            width: '100%',
                            height: '200px',
                            padding: '12px',
                            borderRadius: '6px',
                            border: '1px solid #555',
                            backgroundColor: '#2a3441',
                            color: 'white',
                            fontFamily: "'Sniglet', sans-serif",
                            fontSize: '14px',
                            resize: 'vertical',
                            boxSizing: 'border-box'
                          }}
                        />
                        
                        {/* Word counter */}
                        <div style={{
                          position: 'absolute',
                          bottom: '8px',
                          right: '12px',
                          fontSize: '12px',
                          color: wordCount > 2000 ? '#ff4444' : '#a9a9a9',
                          backgroundColor: 'rgba(0,0,0,0.7)',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          transition: 'color 0.3s ease'
                        }}>
                          {wordCount}/2000
                        </div>
                      </div>

                      {/* Error message */}
                      {diyError && (
                        <div style={{
                          color: '#ff4444',
                          fontSize: '13px',
                          padding: '10px',
                          backgroundColor: 'rgba(255, 68, 68, 0.1)',
                          borderRadius: '6px',
                          border: '1px solid #ff4444'
                        }}>
                          {diyError}
                        </div>
                      )}

                      {/* Button container */}
                      <div style={{ 
                        display: 'flex', 
                        gap: '12px', 
                        justifyContent: 'center', 
                        alignItems: 'center' 
                      }}>
                        {/* Clear button */}
                        <button
                          onClick={handleClearDIY}
                          style={{
                            padding: '12px 24px',
                            backgroundColor: hasContent ? '#E3242B' : '#666',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontFamily: "'Sniglet', sans-serif",
                            fontSize: '16px',
                            fontWeight: 400,
                            transition: 'background-color 0.3s ease'
                          }}
                        >
                          Clear
                        </button>

                        {/* Upload button */}
                        <button
                          onClick={handleDIYUpload}
                          disabled={diyUploading}
                          style={{
                            padding: '12px 24px',
                            backgroundColor: diyUploading ? '#555' : '#348FD9',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: diyUploading ? 'not-allowed' : 'pointer',
                            fontFamily: "'Sniglet', sans-serif",
                            fontSize: '16px',
                            fontWeight: 400
                          }}
                        >
                          {diyUploading ? 'Uploading...' : 'Upload'}
                        </button>
                      </div>

                    </div>
                  )}

                </div>
              </div>
            </div>,
            document.body
          )}

          {/* Beginning instructions text */}
          {!loading && songs.length === 0 && (
          <div
              style={{
              position: "absolute",
              top: centerY !== -1 ? `${centerY}px` : "50%",
              left: centerY !== -1 ? "50%" : "75%",
              transform: "translate(-50%, -50%)",
              color: "white",
              fontSize: 22,
              textAlign: "center",
              fontFamily: "'Sniglet', sans-serif",
              fontWeight: 400,
              zIndex: 8,
              pointerEvents: "none", // so users can't accidentally interact/block hover events below
              width: centerY !== -1 ? "90vw": "40vw",
              maxWidth: 400,
              lineHeight: 1.5,
              transition: 'all 0.3s cubic-bezier(.4,2,.3,1)',
              }}
          >
              <PulseText speed={0.5}>To begin, search a song and click <i>Add</i></PulseText>
          </div>
          )}

          {/* Loading spinner */}
          {loading && (
          <div
              style={{
              position: "absolute",
              top: centerY !== -1 ? `${centerY}px` : "50%",
              left: centerY !== -1 ? "50%" : "75%",
              transform: "translate(-50%, -50%)",
              zIndex: 8,
              pointerEvents: "none",
              width: centerY !== -1 ? "90vw": "40vw",
              maxWidth: 400,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              transition: 'all 0.3s cubic-bezier(.4,2,.3,1)',
              }}
          >
              {/* <div className="ring-spinner-bg">
              <div className="ring-spinner">
                  <div className="ring-spinner-dot"></div>
                  <div className="ring-spinner-dot"></div>
                  <div className="ring-spinner-dot"></div>
                  <div className="ring-spinner-dot"></div>
                  <div className="ring-spinner-dot"></div>
                  <div className="ring-spinner-dot"></div>
                  <div className="ring-spinner-dot"></div>
                  <div className="ring-spinner-dot"></div>
              </div>
            </div> */}
            <div className="orbit-spinner-bg">
              {/* First picture (starts at 0s) */}
              <div className="orbit-container">
                <img src="figure8.png" alt="Figure-8" className="spinning-image" />
              </div>
              {/* Second picture (delayed by 1s) */}
              <div className="orbit-container">
                <img src="figure8-rotated.png" alt="Figure-8" className="spinning-image2" />
              </div>
            </div>

          </div>
          )}

          <Canvas
            id="orbit-canvas"
            camera={{ fov: 70 }}
            onDoubleClick={handleReset}
            onPointerMissed={handleCanvasClick} // Use this instead of onClick
            style={{
              position: 'fixed',
              top: 'env(safe-area-inset-top, 0px)',
              bottom: 0,
              left: 0,
              right: 0,
              width: '100vw',
              background: 'black'
            }}
          >
            <ambientLight intensity={0.8} />
            <directionalLight position={[0, 10, 5]} intensity={0.3} />
            
            {/* Add the position tracker */}
            <ScreenPositionTracker 
              selectedWordInfo={selectedWordInfo}
              onPositionUpdate={setInfoBoxPosition}
            />
            
            {/* Add the camera controller component */}
            <CameraController
              songsLength={songs.length}
              aggregatedPositions={aggregatedPositions}
              controlsRef={controlsRef}
              resetTrigger={resetCameraTrigger}
              onResetComplete={handleResetComplete}
            />
            {aggregatedWords.length !== 0 && (
              <RotatingCloud
                words={aggregatedWords}
                positions={aggregatedPositions}
                colors={aggregatedColors}
                sections={aggregatedSections}
                radii={aggregatedRadii}
                wikiFreqs={aggregatedWikiFreqs}
                selectedWordInfo={selectedWordInfo}
                onWordClick={handleWordClick}
                groupRef={groupRef}
                autoRotate={autoRotate}
              />
            )}
            <OrbitControls
              ref={controlsRef}
              enablePan
              enableRotate
              enableZoom
              onStart={handleDragStart}
            />
          </Canvas>

          {/* Info box */}
          {selectedWordInfo && selectedWordInfo.colors && (
            <div
              ref={infoBoxRef}
              style={{
                position: "absolute",
                left: `${infoBoxPosition.x + 0}px`, // Offset to the right of the sphere
                //top: `${infoBoxPosition.y - (width < 900 ? 15 : 25)}px`,  // Didn't look that good tbh, 15 was too small on mobile
                top: `${infoBoxPosition.y - 25}px`, // Offset above the sphere
                transform: "translate(-50%, -100%)", // Centers horizontally, places above
                backgroundColor: "rgba(0, 0, 0, 0.8)",
                border: `1.5px solid ${REG_BLUE}`,
                borderRadius: "12px",
                padding: "12px 16px",
                color: "white",
                fontFamily: "'Nunito', sans-serif",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.5)",
                zIndex: 15,
                width: "200px",
                minHeight: "50px",
                pointerEvents: "none",
                // Add smooth transition for position changes
                transition: "none",
              }}
            >
              {/* Word Section */}
              <div style={{ 
                fontSize: "18px", 
                fontWeight: "bold", 
                marginBottom: "8px",
                ...(() => {
                  // Ensure colors is always an array
                  const colors = Array.isArray(selectedWordInfo.colors) 
                    ? selectedWordInfo.colors 
                    : [selectedWordInfo.colors || selectedWordInfo.color].filter(Boolean);
                  
                  const textColorInfo = getTextColorInfo(colors);
                  return textColorInfo.isMultiColor ? textColorInfo.gradientStyle : { color: textColorInfo.color };
                })()
              }}>
                {selectedWordInfo.word}
              </div>

              {/* Song Occurrences Section */}
              {selectedWordInfo.songOccurrences && selectedWordInfo.songOccurrences.length > 0 && (
                <div style={{ marginBottom: "8px" }}>
                  <div style={{ 
                    fontSize: "13px", 
                    fontWeight: "bold",
                    color: "#ccc",
                    marginBottom: "4px"
                  }}>
                    Song Occurrences:
                  </div>
                  <ul style={{ 
                    margin: 0, 
                    paddingLeft: "18px",
                    fontSize: "12px",
                    color: "#ddd"
                  }}>
                    {selectedWordInfo.songOccurrences.map((occurrence, index) => (
                      <li key={index} style={{ marginBottom: "2px" }}>
                        {occurrence.songName} (
                        {occurrence.sections.map((section, sectionIndex) => (
                          <React.Fragment key={sectionIndex}>
                            <span style={{ 
                              color: section.color && section.color !== "" ? section.color : "#ddd"
                            }}>
                              {section.name}
                            </span>
                            {sectionIndex < occurrence.sections.length - 1 && ", "}
                          </React.Fragment>
                        ))}
                        )
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Wiki Occurrences Section */}
              <div>
                <div style={{ 
                  fontSize: "13px", 
                  fontWeight: "bold",
                  color: "#ccc",
                  marginBottom: "4px"
                }}>
                  Wiki Occurrences:
                </div>
                <ul style={{ 
                  margin: 0, 
                  paddingLeft: "18px",
                  fontSize: "12px",
                  color: "#ddd"
                }}>
                  <li>
                    {(selectedWordInfo.wikiFreq || 0).toLocaleString('en-US')} ({getWikiFrequencyLabel(selectedWordInfo.wikiFreq || 0)})
                  </li>
                </ul>
              </div>
            </div>
          )}
      </div>
  );
}

// Wraps the points so we can rotate the whole cluster
function RotatingCloud({ 
  groupRef, 
  autoRotate, 
  words, 
  positions, 
  colors, 
  sections, 
  radii,
  wikiFreqs,
  selectedWordInfo,
  onWordClick
}) {

  // Rotation animation unchanged
  useFrame((_, delta) => {
    if (autoRotate && groupRef.current) {
      groupRef.current.rotation.y += delta * 0.0;
    }
  });

  // Helper function for position comparison with tolerance
  const positionsAreClose = (pos1, pos2, epsilon = 0.001) => {
    if (!pos1 || !pos2) return false;
    for (let i = 0; i < 3; i++) {
      if (Math.abs(pos1[i] - pos2[i]) > epsilon) return false;
    }
    return true;
  };

  return (
    <group ref={groupRef}>
      {words && words.length > 0 && words.map((word, i) => {
        // Updated selection logic with better position matching
        const isSelected = selectedWordInfo && 
                          selectedWordInfo.word === word && 
                          positionsAreClose(selectedWordInfo.position, positions[i]);
        
        return (
          <WordPoint
            key={`${word}-${i}`} // Unique key to help React track elements
            position={positions[i]}
            text={word}
            colors={colors[i]} // Pass array of colors
            section={sections[i]}
            radius={radii[i]}
            wikiFreq={wikiFreqs[i]}
            isSelected={isSelected}
            onWordClick={onWordClick}
          />
        );
      })}
    </group>
  );
}

// Point + label
function WordPoint({
  position = [0, 0, 0],
  text,
  colors, // Now an array of colors
  radius = 0.18,
  wikiFreq,
  isSelected = false,
  onWordClick
}) {

  // All hooks at the top, before any conditions
  const textRef = useRef();
  const { camera } = useThree();

  // Check if all colors are transparent
  const validColors = colors.filter(c => c && c !== "" && c !== "transparent");
  const isFullyTransparent = validColors.length === 0;

  // Get color information for text
  const textColorInfo = getTextColorInfo(colors);
  
  const handleClick = useCallback((e) => {
    e.stopPropagation();
    if (onWordClick && !isFullyTransparent) {
      onWordClick(e, {
        word: text,
        wikiFreq: wikiFreq,
        position: position,
        colors: colors // Pass all colors
      });
    }
  }, [onWordClick, isFullyTransparent, text, wikiFreq, position, colors]);

  useFrame(() => {
    if (textRef.current) {
      // Always face the camera in world space
      textRef.current.quaternion.copy(camera.quaternion);
      // Offset label to the right of the sphere, relative to camera
      const right = new THREE.Vector3();
      camera.getWorldDirection(right);
      right.cross(camera.up).normalize();
      right.multiplyScalar(radius * 2); // adjust offset as desired
      textRef.current.position.set(right.x, right.y, right.z);
    }
  });

  if (isFullyTransparent) {
    return null;
  }

  return (
    <group 
      position={position} 
      onClick={handleClick}
      onPointerOver={(e) => !isFullyTransparent && (document.body.style.cursor = 'pointer')}
      onPointerOut={() => (document.body.style.cursor = 'auto')}
    >
      <MultiColorSphere 
        colors={colors}
        radius={radius}
        position={[0, 0, 0]} // Relative to group
        isSelected={isSelected}
      />
      
      {textColorInfo.isMultiColor ? (
        // Multi-color text: use white for better visibility in 3D space
        <Text
          ref={textRef}
          color="#ffffff"
          emissive="#ffffff"
          emissiveIntensity={isSelected ? 0.8 : 0.4}
          fontFamily="'Nunito', sans-serif"
          fontWeight={isSelected ? 'bold' : 'normal'}
          fontSize={Math.max(0.18, radius * 2)}
          anchorX="left"
          anchorY="middle"
          outlineColor="black"
          outlineWidth={0.02}
          // position is set dynamically via useFrame!
        >
          {text}
        </Text>
      ) : (
        // Single color text
        <Text
          ref={textRef}
          color={textColorInfo.color}
          emissive={textColorInfo.color}
          emissiveIntensity={isSelected ? 1.2 : 0.6}
          fontFamily="'Nunito', sans-serif"
          fontWeight={isSelected ? 'bold' : 'normal'}
          fontSize={Math.max(0.18, radius * 2)}
          anchorX="left"
          anchorY="middle"
          outlineColor="black"
          outlineWidth={0.01}
          // position is set dynamically via useFrame!
        >
          {text}
        </Text>
      )}
    </group>
  );
}

// Allows for multi-color sphere
function MultiColorSphere({ colors, radius, position, isSelected }) {
  const groupRef = useRef();
  const { camera } = useThree();

  // Make the sphere's "top" (north pole) always face the camera
  useFrame(() => {
    if (groupRef.current) {
      // Get the direction the camera is looking
      const cameraDirection = new THREE.Vector3();
      camera.getWorldDirection(cameraDirection);

      // The "up" vector represents the north pole of our sphere
      const upVector = new THREE.Vector3(0, 1, 0);

      // Create rotation quaternion to align the north pole with camera direction
      const quat = new THREE.Quaternion();
      quat.setFromUnitVectors(upVector, cameraDirection.negate());
      
      // Smoothly rotate to the target orientation
      groupRef.current.quaternion.slerp(quat, 0.1); // 0.1 controls smoothness
    }
  });

  // If only one color or transparent, use simple sphere
  if (colors.length <= 1) {
    const singleColor = colors[0] || "";
    if (singleColor === "") return null; // Fully transparent

    return (
      <mesh position={position}>
        <sphereGeometry args={[radius, 20, 20]} />
        <meshStandardMaterial
          color={singleColor}
          emissive={singleColor}
          emissiveIntensity={isSelected ? 1.2 : 0.6}
        />
      </mesh>
    );
  }

  // Multi-color sphere with vertical slices
  const sliceAngle = (Math.PI * 2) / colors.length;

  return (
    <group ref={groupRef} position={position}>
      {colors.map((color, index) => {
        if (color === "") {
          // Transparent slice - skip rendering
          return null;
        }

        const startAngle = index * sliceAngle;

        return (
          <mesh key={`slice-${index}-${color}`}>
            <sphereGeometry
              args={[
                radius,
                20,
                20,
                startAngle, // phiStart
                sliceAngle, // phiLength (slice width)
              ]}
            />
            <meshStandardMaterial
              color={color}
              emissive={color}
              emissiveIntensity={isSelected ? 1.2 : 0.6}
              side={THREE.DoubleSide} // Render both sides of the slice
            />
          </mesh>
        );
      })}
    </group>
  );
}

// Handles camera operations within Canvas
function CameraController({ 
  songsLength,
  aggregatedPositions, 
  controlsRef, 
  resetTrigger, 
  onResetComplete 
}) {
  const { camera } = useThree();
  const prevSongsLengthRef = useRef(0);
  const [pendingReset, setPendingReset] = useState(false);

  const resetCameraToPoints = useCallback(() => {
    if (aggregatedPositions.length === 0) {
      // Reset to default if no points
      camera.position.set(...DEFAULT_CAMERA_POS);
      controlsRef.current?.target.set(0, 0, 0);
      controlsRef.current?.update();
      setPendingReset(false);
      if (onResetComplete) onResetComplete();
      return;
    }

    // Calculate centroid
    const centroid = [0, 0, 0];
    aggregatedPositions.forEach(pos => {
      centroid[0] += pos[0];
      centroid[1] += pos[1];
      centroid[2] += pos[2];
    });
    centroid[0] /= aggregatedPositions.length;
    centroid[1] /= aggregatedPositions.length;
    centroid[2] /= aggregatedPositions.length;

    // Calculate max extent from centroid for zoom-out
    let maxExtent = 0;
    aggregatedPositions.forEach(pos => {
      const dx = pos[0] - centroid[0];
      const dy = pos[1] - centroid[1];
      const dz = pos[2] - centroid[2];
      const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (distance > maxExtent) maxExtent = distance;
    });

    // Position camera at centroid + zoom out
    const zoomOut = maxExtent * 1.25 + 5; // padding
    camera.position.set(centroid[0], centroid[1], centroid[2] + zoomOut);

    // Set controls target to centroid
    controlsRef.current?.target.set(...centroid);
    controlsRef.current?.update();

    // Clear pending reset and notify completion
    setPendingReset(false);
    if (onResetComplete) onResetComplete();
  }, [aggregatedPositions, camera, controlsRef, onResetComplete]);

  // Detect when songs length changes and set pending reset
  useEffect(() => {
    if (songsLength !== prevSongsLengthRef.current) {
      prevSongsLengthRef.current = songsLength;
      setPendingReset(true);
    }
  }, [songsLength]); // Only depends on songsLength

  // Execute reset when we have a pending reset and positions are ready
  // Use a small delay to ensure aggregatedPositions have fully updated
  useEffect(() => {
    if (pendingReset && aggregatedPositions.length > 0) {
      // Small delay to ensure positions are fully settled
      const timeoutId = setTimeout(() => {
        resetCameraToPoints();
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [pendingReset, resetCameraToPoints, aggregatedPositions.length]);

  // Handle manual reset trigger (for double-click/double-tap)
  useEffect(() => {
    if (resetTrigger) {
      resetCameraToPoints();
    }
  }, [resetTrigger, resetCameraToPoints]);

  return null;
}


// Main wrapper
function LyricExplorer() {
  const [selectedSongId, setSelectedSongId] = useState(null);
  const [songs, setSongs] = useState([]);
  const [isDashboardCollapsed, setIsDashboardCollapsed] = useState(true); // Renamed for clarity
  const [firstTime, setFirstTime] = useState(true);

  // Lifted states for search input
  const [searchQuery, setSearchQuery] = useState("");  // Controls the input value
  const [selectedSongText, setSelectedSongText] = useState("");  // Controls displayed selected song

  // New state for auto-expanding new songs
  const [newlyAddedSongId, setNewlyAddedSongId] = useState(null);
  
  // Reset handler for Clear button
  function clearAll() {
    setSongs([]);
    setSearchQuery("");  // Reset search input
    setSelectedSongText("");  // Clear selected text
    setSelectedSongId(null);  // Clear selected ID
  }

  function handleSectionColorChange(songId, sectionName, newColor) {
    setSongs(current => current.map(song => {
      if (song.id !== songId) return song;
  
      // Update sectionColors
      const newSectionColors = {
        ...song.sectionColors,
        [sectionName]: newColor
      };
  
      // Explicitly update words' colors for this section
      const updatedWords = song.words.map(w => {
        if (w.section === sectionName) {
          return {
            ...w,
            color: newColor  // Directly set to newColor ("" for transparent)
          };
        }
        return w;
      });
  
      return {
        ...song,
        masterColor: "",  // Clear master to allow section overrides
        sectionColors: newSectionColors,
        words: updatedWords
      };
    }));
  }
  
  function handleMasterColorChange(songId, newMasterColor) {
    setSongs(current => current.map(song => {
      if (song.id !== songId) return song;
  
      const newSectionColors = {};
      Object.keys(song.sections).forEach(section => {
        newSectionColors[section] = newMasterColor; // Set all sections to new master color
      });
  
      // Update words' colors to match
      const updatedWords = song.words.map(w => ({
        ...w,
        color: newMasterColor  // Set to newMasterColor ("" for transparent)
      }));
  
      return { 
        ...song, 
        masterColor: newMasterColor, 
        sectionColors: newSectionColors,
        words: updatedWords
      };
    }));
  }

  function handleDeleteSong(songId) {
    setSongs(current => current.filter(song => song.id !== songId));
  }

  return (
    <>
      <MusicViz
        selectedSongId={selectedSongId}
        setSelectedSongId={setSelectedSongId}
        songs={songs}
        setSongs={setSongs}
        isDashboardCollapsed={isDashboardCollapsed}
        setIsDashboardCollapsed={setIsDashboardCollapsed}
        firstTime={firstTime}
        setFirstTime={setFirstTime}
        searchQuery={searchQuery}  // Pass lifted states
        setSearchQuery={setSearchQuery}
        selectedSongText={selectedSongText}
        setSelectedSongText={setSelectedSongText}
        setNewlyAddedSongId={setNewlyAddedSongId}
      />
      <Dashboard
        songs={songs}
        setSongs={setSongs}
        onSectionColorChange={handleSectionColorChange}
        onMasterColorChange={handleMasterColorChange}
        onDeleteSong={handleDeleteSong}
        collapsed={isDashboardCollapsed}
        setCollapsed={setIsDashboardCollapsed}
        onClear={clearAll}  // Pass reset handler
        newlyAddedSongId={newlyAddedSongId}  // Pass state
        setNewlyAddedSongId={setNewlyAddedSongId}  // Pass setter to reset
      />
    </>
  );
}

export default LyricExplorer;